import React, { FC, useCallback } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Tooltip,
  makeStyles,
  tokens,
  Body1Strong,
  Caption1,
  Link as FluentLink,
  Badge,
} from '@fluentui/react-components';
import { SettingsRegular, DeleteRegular, MoreHorizontal28Regular, WarningFilled } from '@fluentui/react-icons';
import { useParams } from 'react-router-dom';

import { TAssociatedItem, THandleOpenAssociationDialog, TTag, TTagAssociationItemType } from '../types';
import { isTDocument, getItemPathByItemType } from '@modules/documents';
import { TItem, TJurisdictionMap, getJurisdictionId } from '@modules/items';
import { AccessType } from '@modules/access';
import { ControlledTooltip, SmartTableCell } from '@shared/components';
import AssociateButton from './AssociateButton';
import { JURISDICTION_NOT_FOUND } from '../constants';
import { useAssociateTag, useTags } from '../api';
import { TERMINOLOGY } from '@shared/i18n';
import { buildHref } from '@shared/utils';

// -----------------
// Styles
// -----------------
const useStyles = makeStyles({
  tableContainer: {
    overflow: 'hidden',
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  tableBody: {
    '& > :last-child': {
      borderBottom: 'none',
    },
  },
  tableHeaderCellFile: {
    padding: `${tokens.spacingHorizontalS} ${tokens.spacingVerticalL}`,
  },
});

// -----------------
// Helpers
// -----------------

/**
 * Returns a user-friendly name for an item's jurisdiction (if any).
 * If it's a reusable block, returns a special label.
 * If there's no matching jurisdiction, returns a fallback string.
 */
const getJurisdictionNameForItem = (item: TItem, jurisdictionsMap: TJurisdictionMap): string => {
  if (item.isBlock) return TERMINOLOGY.block.upper;

  const jurisId = getJurisdictionId(item);
  if (jurisId === undefined) {
    return JURISDICTION_NOT_FOUND;
  }
  return jurisdictionsMap[jurisId];
};

/**
 * Renders the "Sections" column content.
 * - If item is not a document, or headings are `undefined`, show "All"
 * - If headings is an empty array => Show error badge
 * - Otherwise, list headings in an ordered list with links
 */
const renderSectionsContent = (assoc: TAssociatedItem): React.ReactNode => {
  const { item, headings } = assoc;
  if (!isTDocument(item) || headings === undefined) {
    return 'All';
  }
  if (headings.length === 0) {
    // Headings used to exist, but now deleted => show an error
    return (
      <Badge size="small" icon={<WarningFilled />} iconPosition="before" color="danger" appearance="ghost">
        Associated section has been deleted.
      </Badge>
    );
  }
  // Show a simple list of headings
  return (
    <ol className="py-2">
      {headings.map((h) => (
        <li key={h.id}>
          <FluentLink
            appearance="subtle"
            href={buildHref(`/${getItemPathByItemType(item.type)}/${item._id}`, { elem: h.id })}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            {h.numbering} {h.text}
          </FluentLink>
        </li>
      ))}
    </ol>
  );
};

/**
 * Renders the "More actions" menu for each row, including
 * "Configure sections" and "Dissociate file."
 */
const renderMoreActionsMenu = (
  assoc: TAssociatedItem,
  tag: TTag,
  handleOpenAssociationDialog: THandleOpenAssociationDialog,
  handleDissociateTag: (item: TItem) => void
) => {
  const { item } = assoc;
  const isDoc = isTDocument(item);
  const hasManagePermission = item.accessRight >= AccessType.MANAGE;

  const noManagePermissionTooltip = `You don't have "Manage" access for this item.`;
  const disableConfigureSections = !isDoc || item.isBlock || !hasManagePermission;

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Tooltip content="More actions" relationship="label" withArrow>
          <Button aria-label="More actions" icon={<MoreHorizontal28Regular />} appearance="transparent" size="medium" />
        </Tooltip>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <ControlledTooltip
            content={!hasManagePermission ? noManagePermissionTooltip : 'Sections are only available for documents.'}
            relationship="inaccessible"
            enable={disableConfigureSections}
            withArrow
          >
            <MenuItem
              disabled={disableConfigureSections}
              icon={<SettingsRegular />}
              onClick={() => handleOpenAssociationDialog.heading(tag._id, item)}
            >
              Configure sections
            </MenuItem>
          </ControlledTooltip>

          <ControlledTooltip
            content={noManagePermissionTooltip}
            relationship="inaccessible"
            enable={!hasManagePermission}
            withArrow
          >
            <MenuItem
              disabled={!hasManagePermission}
              icon={<DeleteRegular />}
              onClick={() => handleDissociateTag(item)}
            >
              Dissociate file
            </MenuItem>
          </ControlledTooltip>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

// -----------------
// Main Component
// -----------------

type TProps = {
  tag: TTag;
  items: TAssociatedItem[];
  jurisdictionsMap: TJurisdictionMap;
  handleOpenAssociationDialog: THandleOpenAssociationDialog;
};

/**
 * Table listing associated items for a particular regulatory tag.
 * Each row displays:
 *  - File name (with optional link)
 *  - Jurisdiction name or "Reusable Blocks" label
 *  - Sections (if doc), or "All"
 *  - Action menu (Configure sections, Dissociate)
 */
const AssociatedItemsTable: FC<TProps> = ({ tag, items, jurisdictionsMap, handleOpenAssociationDialog }) => {
  const { id } = useParams();
  const productId = id as string;

  // For re-fetch or invalidation after associating/dissociating
  const { data: tagsData } = useTags(productId);
  const associateTag = useAssociateTag(productId, tagsData);

  const classes = useStyles();

  const handleDissociateTag = useCallback(
    (item: TItem) => {
      associateTag.mutate({
        tagId: tag._id,
        itemId: item._id,
        itemtype: item.type as TTagAssociationItemType,
        dissociateItem: true,
      });
    },
    [associateTag, tag._id]
  );

  return (
    <div className={classes.tableContainer}>
      <Table size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell key="file" className={classes.tableHeaderCellFile}>
              <div className="flex items-center space-x-2">
                <Body1Strong>Associated file</Body1Strong>
                {/* Button for associating new items */}
                <AssociateButton onClick={() => handleOpenAssociationDialog.item(tag._id)} />
              </div>
            </TableHeaderCell>

            <TableHeaderCell key="sections">
              <Body1Strong>Sections</Body1Strong>
            </TableHeaderCell>

            <TableHeaderCell key="settings" style={{ width: '50px' }} />
          </TableRow>
        </TableHeader>

        <TableBody className={classes.tableBody}>
          {items.map((assoc, idx) => {
            const { item } = assoc;
            return (
              <TableRow key={idx}>
                {/* File Column */}
                <SmartTableCell
                  cellProps={{ className: '!pl-4' }}
                  variant={{ kind: 'item', item }}
                  stackedBelow={<Caption1>{getJurisdictionNameForItem(item, jurisdictionsMap)}</Caption1>}
                  padding="medium"
                />

                {/* Sections Column */}
                <TableCell>{renderSectionsContent(assoc)}</TableCell>

                {/* Settings Column (Menu) */}
                <TableCell>
                  {renderMoreActionsMenu(assoc, tag, handleOpenAssociationDialog, handleDissociateTag)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default AssociatedItemsTable;
